<template>
  <b-modal ref="PackageScoreModal" title="Package score" :no-close-on-backdrop="true">
    <div>
      <b-row>
        <b-col>
          <label>{{$t('labels.common.start_date')}}</label>
          <datepicker v-model="scoreData.start_date"
                      lang="en"
                      placeholder=""
                      :width="170"
                      :not-before="new Date()"
                      :first-day-of-week="1"
                      :class="{ required: !scoreData.start_date}"
                      :disabled="!isNewScore || isStartDateEarlyThanToday"
          ></datepicker>
        </b-col>
        <b-col>
          <label>{{$t('labels.common.end_date')}}</label>
          <datepicker v-model="scoreData.end_date"
                      lang="en"
                      placeholder=""
                      :width="170"
                      :not-before="new Date()"
                      :first-day-of-week="1"
          ></datepicker>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <label>{{$t('labels.common.group')}}</label>
          <multiselect v-model="scoreData.groups" :max-height="200" :searchable="true"
                       placeholder="" :multiple="true" :close-on-select="false"
                       label="name" track-by="name" :options="groups" :disabled="isStartDateEarlyThanToday"
          ></multiselect>
        </b-col>
        <b-col>
          <label>{{$t('labels.common.wood')}}</label>
          <multiselect v-model="scoreData.trees" :max-height="200" :searchable="true"
                       placeholder="" :multiple="true" :close-on-select="false"
                       label="name" track-by="name" :options="wood" :disabled="isStartDateEarlyThanToday"
          ></multiselect>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <label title="For setting range value of scoreData enter value with - separator. For example 50 - 100">
            {{$t('labels.common.thickness')}}</label>
          <input class="form-control" placeholder="" v-model="scoreData.thickness"
                 :class="{ required: !scoreData.thickness }"
                 :disabled="isStartDateEarlyThanToday">
        </b-col>
        <b-col>
          <label>{{$t('labels.common.quality')}}</label>
          <multiselect v-model="scoreData.qualities" :max-height="200" :searchable="true"
                       placeholder="" :multiple="true" :close-on-select="false"
                       label="name" track-by="name" :options="qualities_list" :disabled="isStartDateEarlyThanToday"
          ></multiselect>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <label title="For setting range value of width enter value with - separator. For example 50 - 100">
            {{$t('labels.common.width')}}</label>
          <input class="form-control" placeholder="" v-model="scoreData.width"
                 :class="{ required: !scoreData.width }"
                 :disabled="isStartDateEarlyThanToday">
        </b-col>
        <b-col>
          <label>{{$t('labels.common.profile')}}</label>
          <multiselect v-model="scoreData.profiles" :max-height="200" :searchable="true"
                       placeholder="" :multiple="true" :close-on-select="false"
                       label="name" track-by="name" :options="profiles" :disabled="isStartDateEarlyThanToday"
          ></multiselect>
        </b-col>
      </b-row>
      <b-row>
        <b-col title="For setting range value of length enter value with - separator. For example 50 - 100">
          <label>{{$t('labels.common.length')}}</label>
          <input class="form-control" placeholder="" v-model="scoreData.length"
                 :class="{ required: !scoreData.length }"
                 :disabled="isStartDateEarlyThanToday">
        </b-col>
        <b-col>
          <label>{{$t('labels.common.score')}}</label>
          <input type="number" class="form-control" v-model="scoreData.score"
                 placeholder="" :disabled="isStartDateEarlyThanToday">
        </b-col>
      </b-row>
    </div>
    <div slot="modal-footer" class="w-100 text-center">
      <b-btn size="sm" variant="success" @click="save">
        {{$t('buttons.common.save')}}
      </b-btn>
      <b-btn size="sm" variant="outline-success" @click="saveAndMore" class="ml-1">
        {{$t('buttons.common.save_add_more')}}
      </b-btn>
    </div>
  </b-modal>
</template>

<script>
  import { mapGetters } from 'vuex';
  import Multiselect from 'vue-multiselect';
  import Datepicker from 'vue2-datepicker';
  import moment from 'moment';
  import { handlerResponse, setNotifyData, getArrayByProperty } from '@/helpers/commonHelpers';
  import { notificationsRenderer } from '@/helpers/renderHelpers';
  import { DATE_FORMAT } from '@/constants';

  export default {
    components: {
      Multiselect,
      Datepicker
    },
    data() {
      return {
        initialScoreData: {
          start_date: null,
          end_date: null,
          groups: [],
          width: null,
          length: null,
          thickness: null,
          trees: [],
          qualities: [],
          profiles: [],
          score: 0
        },
        scoreData: {}
      }
    },
    methods: {
      prepareSavedData() {
        const payload = {
          end_date: this.scoreData.end_date ? moment(this.scoreData.end_date).format(DATE_FORMAT) : null,
          width: this.createRangeParam(this.scoreData.width),
          length: this.createRangeParam(this.scoreData.length),
          thickness: this.createRangeParam(this.scoreData.thickness),
          profiles: getArrayByProperty(this.scoreData.profiles, 'id'),
          trees: getArrayByProperty(this.scoreData.trees, 'id'),
          qualities: getArrayByProperty(this.scoreData.qualities, 'id'),
          groups: getArrayByProperty(this.scoreData.groups, 'id'),
          process: this.additionalData.process,
          score: this.scoreData.score
        }
        if (this.isNewScore) {
          payload.start_date = this.scoreData.start_date ? moment(this.scoreData.start_date).format(DATE_FORMAT) : null;
        }
        return payload;
      },
      createRangeParam(value) {
        const paramsValue = value && value.toString();
        if (value) {
          const startValue = paramsValue.split('-')[0], endValue = paramsValue.split('-')[1] || startValue;
          return [Number(startValue.trim()), Number(endValue.trim())];
        }
        return null;
      },
      updateScoreCb(message) {
        this.$notify(setNotifyData(this.$i18n.t(`notifications.common.success`),
          message, 'success'));
        this.$store.dispatch('woodware/loadPackagesScores', this.additionalData);
        this.$refs['PackageScoreModal'].hide();
      },
      async save() {
        const payload = this.prepareSavedData();
        const loader = this.$loading.show();
        try {
          let response;
          let saveCbMessage;
          if (this.isNewScore) {
            response = await this.$http.post(`woodware/prices/packages_scores/`, payload);
            saveCbMessage = this.$i18n.t(`notifications.prices.package_score_created`);
          } else {
            const scoreId = this.scoreData.id;
            response = await this.$http.put(`woodware/prices/packages_scores/${scoreId}/`, payload);
            saveCbMessage = this.$i18n.t(`notifications.prices.package_score_updated`);
          }
          const data = await handlerResponse(response);
          this.scoreData = {...data};
          this.updateScoreCb(saveCbMessage);
        } catch (e) {
          console.log(e);
          notificationsRenderer(e.data, 'error');
        } finally {
          loader.hide();
        }
      },
      async saveAndMore() {
        await this.save();
        this.scoreData.id = null;
        this.scoreData.start_date = null;
        this.scoreData.end_date = null;
        this.scoreData.score = 0;
        this.$refs['PackageScoreModal'].show();
      }
    },
    computed: {
      ...mapGetters('woodware', ['wood', 'qualities_list', 'profiles', 'groups']),
      isNewScore() {
        return !this.scoreData.id;
      },
      isStartDateEarlyThanToday() {
        return !this.isNewScore && (moment(this.scoreData.start_date)
          < moment(new Date()));
      }
    },
    mounted() {
      this.$root.$on('openPackageScoreModal', (data) => {
        this.scoreData = {...this.initialScoreData};
        this.additionalData = data.additionalData;
        this.scoreData = {...data.scoreData};
        if (this.$refs['PackageScoreModal']) {
          this.$store.cache.dispatch('woodware/getProfiles');
          this.$store.cache.dispatch('woodware/getWood');
          this.$store.cache.dispatch('woodware/getQualitiesList');
          this.$store.cache.dispatch('woodware/getGroupsWithHierarchy');
          this.$refs['PackageScoreModal'].show();
        }
      });
    },
    beforeDestroy() {
      this.$root.$off('openPackageScoreModal');
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/modals/PackageScoreModal.vue